import { Button, Flex, Link, Tag } from '@chakra-ui/react';
import {
  ListingForSearchMlsListingFragment,
  MlsListingSearchResultFragment,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import { ComponentProps } from 'react';
import {
  getIsUserListing,
  getListingDisplayStatus,
  getListingPrice,
} from '~/apps/consumer/utils/listing.utils';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { useMapCardActions } from '../../MapSearch/hooks/useMapCardActions';
import { PremiumTag } from '../../PremiumTag/PremiumTag';
import { BaseListingCard } from './BaseListingCard';
import { HorizontalListingCard } from './HorizontalListingCard';

interface PublicListingCardProps
  extends Omit<ComponentProps<typeof BaseListingCard>, 'listing'> {
  listing?: ListingForSearchMlsListingFragment;
  mlsListing?: MlsListingSearchResultFragment;
  isEmbedded?: boolean;
  fullWidth?: boolean;
  utmParams?: { [key: string]: string };
  showHorizontalMobileCard?: boolean;
}

export const PublicListingCard = ({
  listing,
  mlsListing,
  isEmbedded = false,
  fullWidth = false,
  utmParams,
  showHorizontalMobileCard = false,
  ...otherProps
}: PublicListingCardProps) => {
  const isMobile = useIsMobile();

  const { user } = useAppContext();

  const showHorizontal = showHorizontalMobileCard && isMobile && mlsListing;

  const { onCardClick, onStartOfferScorecardClick, isCreatingBOA } =
    useMapCardActions({
      isEmbedded,
      utmParams,
    });

  const buttons = mlsListing && (
    <Flex direction="column" gap={0}>
      <Button
        _hover={{
          textDecoration: 'none',
          bgColor: 'primaryButton.300', // Somehow setting textDecoration to none removes the hover bg color, so manually setting it
        }}
        as={Link}
        borderRadius="sm"
        fontSize="sm"
        isExternal={true}
        mt={2}
        size="xs"
        variant="solid"
        onClick={(e) => {
          e.stopPropagation();
          onCardClick({ listing, mlsListing });
        }}
      >
        See listing offer data
      </Button>
      <Button
        borderColor={showHorizontal ? 'indigo.900' : undefined}
        borderRadius="sm"
        color={showHorizontal ? 'indigo.900' : undefined}
        fontSize="sm"
        isLoading={isCreatingBOA}
        mt={2}
        size="xs"
        variant="outline"
        onClick={(e) => {
          e.stopPropagation();
          void onStartOfferScorecardClick({ listing, mlsListing });
        }}
      >
        Create Offer Scorecard
      </Button>
    </Flex>
  );

  // display the mls status if only mls listing is provided

  const isUserListing = getIsUserListing(
    listing as PublicListingFragment,
    user
  );

  const price = getListingPrice(mlsListing);

  const topTags = (
    <Flex alignItems="center" gap={1}>
      {isUserListing ? <PremiumTag label="My Listing" /> : undefined}
      {mlsListing?.mls?.statusText ? (
        <Tag lineHeight={1} variant="dark">
          {getListingDisplayStatus({ mlsListing })}
        </Tag>
      ) : undefined}
    </Flex>
  );

  if (showHorizontal) {
    return (
      <HorizontalListingCard
        bottomButton={buttons}
        isHoverable={false}
        mlsListing={mlsListing}
      />
    );
  }

  return (
    <>
      <BaseListingCard
        area={mlsListing?.property?.area}
        bathrooms={mlsListing?.property?.bathrooms}
        bedrooms={mlsListing?.property?.bedrooms}
        button={buttons}
        city={mlsListing?.address?.city}
        daysOnMarket={mlsListing?.mls?.daysOnMarket}
        fullWidth={fullWidth}
        office={mlsListing?.office?.name}
        photoSrc={mlsListing?.photos?.[0]}
        price={price}
        state={mlsListing?.address?.state}
        streetName={mlsListing?.address?.streetName}
        streetNumberText={mlsListing?.address?.streetNumberText}
        topTags={topTags}
        unit={mlsListing?.address?.unit}
        zipCode={mlsListing?.address?.postalCode}
        {...otherProps}
      />
    </>
  );
};
